import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { benevolentAPI, uploadAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Heart,
  Plus,
  Phone,
  Calendar,
  DollarSign,
  FileText,
  Upload,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  Send,
  AlertCircle,
  Lock,
  Trash2,
  Archive
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import notificationSound from '../utils/notificationSound';

const BenevolentPage = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const prevRequestCount = useRef(0);
  const isFirstLoad = useRef(true);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const [formData, setFormData] = useState({
    phone: '',
    needType: '',
    otherDescription: '',
    amountNeeded: '',
    dateNeeded: '',
    description: '',
    attachmentUrl: ''
  });

  const [statusForm, setStatusForm] = useState({
    status: '',
    statusNote: '',
    approvedAmount: ''
  });

  useEffect(() => {
    fetchData();
    
    // Poll for new requests every 30 seconds
    const pollInterval = setInterval(() => {
      fetchData();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, []);

  const fetchData = async () => {
    try {
      const [requestsData, accessData] = await Promise.all([
        benevolentAPI.getAll(),
        benevolentAPI.canManage()
      ]);
      
      // Play sound if new requests came in (for managers, not on first load)
      if (!isFirstLoad.current && accessData.canManage && requestsData.length > prevRequestCount.current) {
        notificationSound.playBenevolentSound();
      }
      prevRequestCount.current = requestsData.length;
      isFirstLoad.current = false;
      
      setRequests(requestsData);
      setCanManage(accessData.canManage);
      setCanApprove(accessData.canApprove || false);
      
      // Mark member's requests as viewed when they open the page
      await benevolentAPI.markViewed();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadAPI.uploadFile(file);
      // Cloudinary returns full URLs, local uploads return relative URLs
      const fullUrl = result.url.startsWith('http') 
        ? result.url 
        : `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      setFormData({ ...formData, attachmentUrl: fullUrl });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const newRequest = await benevolentAPI.create({
        ...formData,
        amountNeeded: parseFloat(formData.amountNeeded)
      });
      setRequests([newRequest, ...requests]);
      setSubmitSuccess(true);
      
      setTimeout(() => {
        setShowCreateDialog(false);
        setSubmitSuccess(false);
        setFormData({
          phone: '',
          needType: '',
          otherDescription: '',
          amountNeeded: '',
          dateNeeded: '',
          description: '',
          attachmentUrl: ''
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedRequest || !statusForm.status) return;
    
    // Validate partial approval has an amount
    if (statusForm.status === 'partial' && !statusForm.approvedAmount) {
      alert('Please enter the approved amount for partial approval.');
      return;
    }
    
    setStatusUpdating(true);
    try {
      const updated = await benevolentAPI.updateStatus(
        selectedRequest.id,
        statusForm.status,
        statusForm.statusNote,
        statusForm.status === 'partial' ? parseFloat(statusForm.approvedAmount) : null
      );
      setRequests(requests.map(r => r.id === selectedRequest.id ? updated : r));
      setSelectedRequest(updated);
      setStatusForm({ status: '', statusNote: '', approvedAmount: '' });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to permanently delete this request?')) {
      return;
    }
    
    setActionLoading('delete');
    try {
      await benevolentAPI.delete(requestId);
      setRequests(requests.filter(r => r.id !== requestId));
      setShowDetailDialog(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Failed to delete request. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (requestId) => {
    setActionLoading('archive');
    try {
      await benevolentAPI.archive(requestId);
      setRequests(requests.filter(r => r.id !== requestId));
      setShowDetailDialog(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error archiving request:', error);
      alert('Failed to archive request. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (requestStatus, approvedAmount = null) => {
    const styles = {
      pending: { bg: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      reviewed: { bg: 'bg-blue-100 text-blue-800', icon: Eye, label: 'Reviewed' },
      approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
      partial: { bg: 'bg-orange-100 text-orange-800', icon: AlertCircle, label: 'Partially Approved' },
      denied: { bg: 'bg-red-100 text-red-800', icon: XCircle, label: 'Denied' }
    };
    const style = styles[requestStatus] || styles.pending;
    const Icon = style.icon;
    const label = style.label + (requestStatus === 'partial' && approvedAmount ? ` ($${approvedAmount.toLocaleString()})` : '');
    return (
      <Badge className={`${style.bg} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-8 pb-8 rounded-b-3xl shadow-xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
            <Heart className="w-6 h-6 text-tan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Benevolent Ministry</h1>
            <p className="text-tan-400 text-sm font-medium">
              {canManage ? 'Manage assistance requests' : 'Assistance Request'}
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* Info Card */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-tan-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-tan-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-medium text-navy-900 mb-1">Confidential Assistance</p>
                <p>All requests are kept strictly confidential and are only viewable by the Benevolent Ministry team.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Deacon Mike Card */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Questions? Contact</p>
                <p className="font-bold text-navy-900">Deacon Mike Leydig</p>
              </div>
              <a
                href="tel:+15597904492"
                className="flex items-center gap-2 bg-tan-500 hover:bg-tan-600 text-white px-4 py-2 rounded-xl font-medium transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Submit Request Button - Available for all members */}
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white rounded-xl font-semibold text-base shadow-md"
          data-testid="submit-benevolent-request-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Submit Assistance Request
        </Button>

        {/* Request List */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-bold text-navy-900">
                {canManage ? 'All Requests' : 'My Requests'}
              </h3>
            </div>
            <ScrollArea className="max-h-[calc(100vh-400px)]">
              <div className="divide-y divide-gray-100">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedRequest(request);
                      setShowDetailDialog(true);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        {canManage && (
                          <p className="font-bold text-navy-900 mb-1">{request.userName}</p>
                        )}
                        <p className="text-sm font-medium text-gray-800">{request.needType}</p>
                        <p className="text-xs text-gray-500">{formatDate(request.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(request.requestStatus, request.approvedAmount)}
                        <p className="text-sm font-bold text-navy-900 mt-1">
                          {formatCurrency(request.amountNeeded)}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{request.description}</p>
                  </div>
                ))}

                {requests.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <Heart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{canManage ? 'No requests yet' : 'You haven\'t submitted any requests'}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Create Request Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900 flex items-center gap-2">
              <Heart className="w-5 h-5 text-tan-600" />
              Request Assistance
            </DialogTitle>
          </DialogHeader>

          {submitSuccess ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-lg font-bold text-gray-900">Request Submitted!</p>
              <p className="text-sm text-gray-500 mt-2">
                Your request has been sent confidentially to the Benevolent Ministry.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-3">Need to speak with someone?</p>
                <a
                  href="tel:+15597904492"
                  className="inline-flex items-center gap-2 bg-tan-500 hover:bg-tan-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Call Deacon Mike
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>This information is kept strictly confidential.</span>
              </div>

              <div className="space-y-2">
                <Label className="text-navy-900 font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-tan-600" />
                  Contact Phone Number
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  placeholder="(559) 555-1234"
                  className="h-11 bg-gray-50 border-gray-200 rounded-xl"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-navy-900 font-medium">Type of Need</Label>
                <Select
                  value={formData.needType}
                  onValueChange={(value) => setFormData({ ...formData, needType: value })}
                >
                  <SelectTrigger className="h-11 bg-gray-50 border-gray-200 rounded-xl">
                    <SelectValue placeholder="Select type of need" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rent">Rent</SelectItem>
                    <SelectItem value="Electric Bill">Electric Bill</SelectItem>
                    <SelectItem value="Food">Food</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.needType === 'Other' && (
                <div className="space-y-2">
                  <Label className="text-navy-900 font-medium">Describe the Need</Label>
                  <Input
                    name="otherDescription"
                    placeholder="Please describe"
                    className="h-11 bg-gray-50 border-gray-200 rounded-xl"
                    value={formData.otherDescription}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-navy-900 font-medium flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-tan-600" />
                    Amount Needed
                  </Label>
                  <Input
                    name="amountNeeded"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="h-11 bg-gray-50 border-gray-200 rounded-xl"
                    value={formData.amountNeeded}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-navy-900 font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-tan-600" />
                    Date Needed By
                  </Label>
                  <Input
                    name="dateNeeded"
                    type="date"
                    className="h-11 bg-gray-50 border-gray-200 rounded-xl"
                    value={formData.dateNeeded}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-navy-900 font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-tan-600" />
                  Description / Additional Details
                </Label>
                <textarea
                  name="description"
                  placeholder="Please provide any additional details about your situation..."
                  className="w-full min-h-[100px] px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-navy-900 font-medium flex items-center gap-2">
                  <Upload className="w-4 h-4 text-tan-600" />
                  Attach Document (Optional)
                </Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*,.pdf"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 rounded-xl"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploading ? 'Uploading...' : formData.attachmentUrl ? 'File Attached ✓' : 'Upload Bill or Document'}
                </Button>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-11 rounded-xl"
                  onClick={() => setShowCreateDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 bg-tan-500 hover:bg-tan-600 text-white rounded-xl"
                  disabled={submitting || !formData.needType}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">Request Details</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedRequest.requestStatus, selectedRequest.approvedAmount)}
                <span className="text-lg font-bold text-navy-900">
                  {formatCurrency(selectedRequest.amountNeeded)}
                </span>
              </div>

              {canManage && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500">Requested by</p>
                  <p className="font-bold text-navy-900">{selectedRequest.userName}</p>
                  <p className="text-sm text-gray-600">{selectedRequest.phone}</p>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Type of Need</p>
                  <p className="font-medium text-navy-900">
                    {selectedRequest.needType}
                    {selectedRequest.otherDescription && ` - ${selectedRequest.otherDescription}`}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date Needed By</p>
                  <p className="font-medium text-navy-900">{formatDate(selectedRequest.dateNeeded)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="text-gray-700">{selectedRequest.description}</p>
                </div>
                {selectedRequest.attachmentUrl && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Attachment</p>
                    <a
                      href={selectedRequest.attachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-tan-600 hover:text-tan-700"
                    >
                      <FileText className="w-4 h-4" />
                      View Document
                    </a>
                  </div>
                )}
                {selectedRequest.statusNote && (
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-500">Status Note</p>
                    <p className="text-gray-700">{selectedRequest.statusNote}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Submitted</p>
                  <p className="text-gray-600">{formatDate(selectedRequest.createdAt)}</p>
                </div>
              </div>

              {/* Status Update Form - Only for managers */}
              {canManage && (
                <div className="border-t border-gray-200 pt-4 mt-4 space-y-3">
                  <h4 className="font-bold text-navy-900">Update Status</h4>
                  <Select
                    value={statusForm.status}
                    onValueChange={(value) => setStatusForm({ ...statusForm, status: value })}
                  >
                    <SelectTrigger className="h-11 bg-gray-50 border-gray-200 rounded-xl">
                      <SelectValue placeholder="Select new status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      {canApprove && (
                        <>
                          <SelectItem value="approved">Approved (Full Amount)</SelectItem>
                          <SelectItem value="partial">Partially Approved</SelectItem>
                          <SelectItem value="denied">Denied</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  {!canApprove && (
                    <p className="text-xs text-gray-500 italic">
                      Note: Only Deacon Mike and Anessa can approve or deny requests.
                    </p>
                  )}
                  
                  {/* Approved Amount field - only show for partial approvals */}
                  {statusForm.status === 'partial' && (
                    <div className="space-y-1">
                      <Label className="text-sm text-gray-600">Approved Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter approved amount"
                        className="h-11 bg-gray-50 border-gray-200 rounded-xl"
                        value={statusForm.approvedAmount}
                        onChange={(e) => setStatusForm({ ...statusForm, approvedAmount: e.target.value })}
                      />
                      <p className="text-xs text-gray-500">
                        Requested: ${selectedRequest?.amountNeeded?.toLocaleString() || '0'}
                      </p>
                    </div>
                  )}
                  
                  <Input
                    placeholder="Add a note (optional)"
                    className="h-11 bg-gray-50 border-gray-200 rounded-xl"
                    value={statusForm.statusNote}
                    onChange={(e) => setStatusForm({ ...statusForm, statusNote: e.target.value })}
                  />
                  <Button
                    className="w-full h-11 bg-navy-900 hover:bg-navy-800 text-white rounded-xl"
                    onClick={handleStatusUpdate}
                    disabled={!statusForm.status || statusUpdating || (statusForm.status === 'partial' && !statusForm.approvedAmount)}
                  >
                    {statusUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Status'}
                  </Button>
                  
                  {/* Archive and Delete Actions */}
                  <div className="flex gap-2 pt-2 border-t border-gray-200 mt-3">
                    <Button
                      variant="outline"
                      className="flex-1 h-10 rounded-xl text-gray-600 hover:bg-gray-100"
                      onClick={() => handleArchive(selectedRequest.id)}
                      disabled={actionLoading === 'archive'}
                    >
                      {actionLoading === 'archive' ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Archive className="w-4 h-4 mr-2" />
                      )}
                      Archive
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 h-10 rounded-xl text-red-600 hover:bg-red-50 border-red-200"
                      onClick={() => handleDelete(selectedRequest.id)}
                      disabled={actionLoading === 'delete'}
                    >
                      {actionLoading === 'delete' ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default BenevolentPage;
